import { useMutation, useQuery, useQueryClient } from "react-query";
import { apiConfig } from "../config/apiConfig";
import apiService from "../service/apiService";
import { useCookie } from "./useCookie";
import { useCallback } from "react";

export function useUserData(
  id: string,
  rideFilters: any,
  rideReviewsFilters: any,
  BookingReviewsFilters: any,
  pointsFilters: any,
) {
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();

  const {
    data: userData,
    isLoading: isLoadingUserData,
    error: userError,
  } = useQuery<User, Error>(["user", id], async () => {
    const endpoint = apiConfig.users.get(id);
    const response = await apiService.MakeGetRequest(endpoint, token);
    return response;
  });

  const invalidateQueryUserInfoQuery = useCallback(() => {
    queryClient.invalidateQueries(["user", id]);
  }, [id, queryClient]);

  const {
    data: userBookingReviewsData,
    isLoading: isLoadingUserBookingReviews,
    error: userBookingReviewsError,
  } = useQuery(["user", id, BookingReviewsFilters], async () => {
    const endpoint = apiConfig.reviews.user_booking_reviews(
      id,
      BookingReviewsFilters,
    );
    const response = await apiService.MakeGetRequest(endpoint, token);
    return response;
  });

  const {
    data: ridesData,
    isLoading: isLoadingRides,
    error: ridesError,
  } = useQuery(["userRides", rideFilters], async () => {
    const endpoint = apiConfig.rides.all(rideFilters);
    const response = await apiService.MakeGetRequest(endpoint, token);

    return response;
  });

  const { mutateAsync: deleteReview, isLoading: isDeletingReview } =
    useMutation(
      async (reviewId: string) => {
        const endpoint = apiConfig.reviews.delete(reviewId);
        const response = await apiService.MakeDeleteRequest(endpoint, token);
        return response;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["user", id, BookingReviewsFilters]);
          queryClient.invalidateQueries([
            "userRidesreviews",
            rideReviewsFilters,
          ]);
        },
      },
    );

  const { mutateAsync: deleteRideReview, isLoading: isDeletingRideReview } =
    useMutation(
      async (rideReviewId: string) => {
        const endpoint = apiConfig.reviews.deleteRideReview(rideReviewId);
        const response = await apiService.MakeDeleteRequest(endpoint, token);
        return response;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["user", id, BookingReviewsFilters]);
          queryClient.invalidateQueries([
            "userRidesreviews",
            rideReviewsFilters,
          ]);
        },
      },
    );

  const {
    data: UserRidesreviewsData,
    isLoading: isLoadingUserRidesreviews,
    error: UserRidesreviewsError,
  } = useQuery(["userRidesreviews", rideReviewsFilters], async () => {
    const endpoint = apiConfig.reviews.user_rides_reviews(
      id,
      rideReviewsFilters,
    );
    const response = await apiService.MakeGetRequest(endpoint, token);

    return response;
  });

  const {
    data: userPointsHistoryData,
    isLoading: isLoadingUserPointsHistory,
    error: userPointsHistoryError,
  } = useQuery(
    ["userPointsHistory", id, pointsFilters], 
    async () => {
      const endpoint = apiConfig.users.points(id, pointsFilters);
      const response = await apiService.MakeGetRequest(endpoint, token);
      return response;
    },
    {
      keepPreviousData: true,
    },
  );

  // console.log("🚀 ~ file: useUserData.ts:103 ~ userPointsHistoryData:", userPointsHistoryData);

  const isLoading =
    isLoadingUserBookingReviews ||
    isLoadingRides ||
    isLoadingUserRidesreviews ||
    isLoadingUserData ||
    isLoadingUserPointsHistory;

  return {
    userBookingReviewsData: userBookingReviewsData,
    ridesdata: ridesData,
    rides: ridesData || {
      items: [],
      meta: { totalItems: 0, totalPages: 1, currentPage: 1 },
    },
    UserRidesreviewsData: UserRidesreviewsData,
    UserRidesreviewsError: UserRidesreviewsError,
    userPointsHistoryData: userPointsHistoryData,
    userPointsHistoryError: userPointsHistoryError,
    isLoading,
    userError,
    userBookingReviewsError,
    userData,
    ridesError,
    deleteReview,
    isDeletingReview,
    deleteRideReview,
    isDeletingRideReview,
    invalidateQueryUserInfoQuery,
  };
}
